import { motion } from "framer-motion";
import { MagnetizeButton } from "./MagnetizeButton";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function ElegantShape({ className, delay = 0, width = 400, height = 100, rotate = 0, gradient = "from-white/[0.08]" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -150, rotate: rotate - 15 }}
      animate={{ opacity: 1, y: 0, rotate: rotate }}
      transition={{ duration: 2.4, delay, ease: [0.23, 0.86, 0.39, 0.96], opacity: { duration: 1.2 } }}
      className={cn("absolute", className)}
    >
      <motion.div
        animate={{ y: [0, 15, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        style={{ width, height }}
        className="relative"
      >
        <div className={cn("absolute inset-0 rounded-full", "bg-gradient-to-r to-transparent", gradient, "backdrop-blur-[2px] border-2 border-white/[0.15]", "shadow-[0_8px_32px_0_rgba(255,255,255,0.1)]", "after:absolute after:inset-0 after:rounded-full", "after:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.2),transparent_70%)]")} />
      </motion.div>
    </motion.div>
  );
}

function ShapeLandingHero({
  badge = "Now in Beta",
  title1 = "The desk,",
  title2 = "not the portal.",
  subtitle = "Finance internships across Europe. One profile. We fill employer forms in your browser — you review and submit.",
  onCTA,
}) {
  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 1, delay: 0.5 + i * 0.2, ease: [0.25, 0.4, 0.25, 1] } }),
  };

  return (
    <div style={{ background: "#fff", padding: "var(--hero-inset, 16px)" }}>
    <div className="relative w-full flex items-center justify-center overflow-hidden" style={{ background: "#0B1220", borderRadius: "var(--hero-radius, 28px)", minHeight: "calc(100vh - var(--hero-inset, 16px) * 2)" }}>
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 30% 20%, #162033 0%, #0B1220 55%, #070B14 100%)" }} />
      <div className="absolute inset-0 bg-gradient-to-br from-teal-600/[0.08] via-transparent to-slate-500/[0.06] blur-3xl" />
      <div className="absolute inset-0 overflow-hidden">
        <ElegantShape delay={0.3} width={600} height={140} rotate={12} gradient="from-teal-500/[0.14]" className="left-[-10%] md:left-[-5%] top-[15%] md:top-[20%]" />
        <ElegantShape delay={0.5} width={500} height={120} rotate={-15} gradient="from-slate-400/[0.12]" className="right-[-5%] md:right-[0%] top-[70%] md:top-[75%]" />
        <ElegantShape delay={0.4} width={300} height={80} rotate={-8} gradient="from-cyan-600/[0.1]" className="left-[5%] md:left-[10%] bottom-[5%] md:bottom-[10%]" />
        <ElegantShape delay={0.6} width={200} height={60} rotate={20} gradient="from-teal-400/[0.12]" className="right-[15%] md:right-[20%] top-[10%] md:top-[15%]" />
        <ElegantShape delay={0.7} width={150} height={40} rotate={-25} gradient="from-slate-300/[0.1]" className="left-[20%] md:left-[25%] top-[5%] md:top-[10%]" />
      </div>

      <div className="relative z-10 container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center">

          <motion.div custom={0} variants={fadeUpVariants} initial="hidden" animate="visible" className="flex justify-center mb-8 md:mb-10">
            <img src="/tandm-logo-on-dark.png" alt="Tandm" style={{ height: 72, objectFit: "contain" }} />
          </motion.div>

          <motion.div
            custom={1} variants={fadeUpVariants} initial="hidden" animate="visible"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8 md:mb-10 cursor-default"
            style={{ background: "rgba(78,108,126,0.2)", border: "1px solid rgba(108,150,156,0.4)" }}
            whileHover={{ scale: 1.05, backgroundColor: "rgba(78,108,126,0.32)", transition: { duration: 0.2 } }}
          >
            <motion.span
              animate={{ scale: [1, 1.4, 1], opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              style={{ width: 8, height: 8, borderRadius: "50%", background: "#6C969C", display: "inline-block", flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, color: "rgba(232,241,243,0.9)", letterSpacing: "0.06em", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600 }}>
              {badge}
            </span>
          </motion.div>

          <motion.div custom={2} variants={fadeUpVariants} initial="hidden" animate="visible">
            <h1
              className="mb-6 md:mb-8 tracking-tight"
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontWeight: 500,
                fontSize: "clamp(40px, 8vw, 72px)",
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
                color: "#F4F6F7",
              }}
            >
              {title1}
              <br />
              <span style={{ background: "linear-gradient(135deg,#7CACB7,#6C969C 40%,#4E6C7E)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                {title2}
              </span>
            </h1>
          </motion.div>

          <motion.div custom={3} variants={fadeUpVariants} initial="hidden" animate="visible">
            <p
              className="mb-10 max-w-xl mx-auto px-4"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: "clamp(16px, 2vw, 18px)", lineHeight: 1.6, color: "rgba(148,163,184,0.95)", fontWeight: 400 }}
            >
              {subtitle}
            </p>
          </motion.div>

          <motion.div custom={4} variants={fadeUpVariants} initial="hidden" animate="visible" className="flex justify-center">
            <MagnetizeButton onClick={onCTA} particleCount={14} style={{ fontSize: 15, padding: "15px 40px" }}>
              Get Early Access
            </MagnetizeButton>
          </motion.div>

        </div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-transparent to-[#0B1220]/70 pointer-events-none" />
    </div>
    </div>
  );
}

export { ShapeLandingHero };
